import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaSpinner } from 'react-icons/fa';
import { CloseModal } from '../../sites/closemodal';

interface TypeFormData {
  training_name: string;
  training_description: string;
  validity_months: string;
}

interface AddTypeOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading?: boolean;
}

export const AddTypeOffcanvas: React.FC<AddTypeOffcanvasProps> = ({ isOpen, onClose, onSubmit, loading = false }) => {
  const [formData, setFormData] = useState<TypeFormData>({ training_name: '', training_description: '', validity_months: '' });
  const [errors, setErrors] = useState<Partial<TypeFormData>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({ training_name: '', training_description: '', validity_months: '' });
      setErrors({});
      setShowCloseModal(false);
    }
  }, [isOpen]);

  const requestClose = () => {
    if (loading) return;
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<TypeFormData> = {};
    if (!formData.training_name.trim()) newErrors.training_name = 'Course name is required';
    if (formData.validity_months && (isNaN(Number(formData.validity_months)) || Number(formData.validity_months) < 1)) newErrors.validity_months = 'Must be a positive number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof TypeFormData]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData: any = { training_name: formData.training_name };
      if (formData.training_description.trim()) submitData.training_description = formData.training_description;
      if (formData.validity_months) submitData.validity_months = Number(formData.validity_months);
      onSubmit(submitData);
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading) requestClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? requestClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">Add Course</h2>
            <p className="text-sm text-gray-600 mt-1">Define a new course.</p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
              <input type="text" name="training_name" value={formData.training_name} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.training_name ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="e.g., ESTAC" />
              {errors.training_name && <p className="mt-1 text-sm text-red-600">{errors.training_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea name="training_description" value={formData.training_description} onChange={handleInputChange} disabled={loading} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none" placeholder="Training description" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Validity (Months)</label>
              <input type="number" name="validity_months" value={formData.validity_months} onChange={handleInputChange} disabled={loading} className={`w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 text-sm ${errors.validity_months ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} placeholder="e.g., 12" min="1" />
              {errors.validity_months && <p className="mt-1 text-sm text-red-600">{errors.validity_months}</p>}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={requestClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={loading || !formData.training_name.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {loading ? (<><FaSpinner className="w-4 h-4 animate-spin" />Creating...</>) : (<><FaSave className="w-4 h-4" />Create Course</>)}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};
